// src/components/TermsModal.js
import React, { useRef, useState, useEffect } from 'react';

/**
 * A modal that displays the T&C and Privacy Policy in a scrollable container.
 * The Accept button only becomes enabled after the user scrolls to the bottom.
 */
function TermsModal({ onClose, onAccept }) {
  const scrollContainerRef = useRef(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // The full text of T&C and Privacy Policy:
  const termsText = `
TT INTERNATIONAL SERVICE TERMS AND CONDITIONS

1. Introduction
Welcome to TT International. By accessing and using our platform, you agree to abide by the following Service Terms and Conditions ("Terms"). These Terms govern your relationship with TT International ("TT," "we," "our," "us") regarding the services we provide, including ticket advisory, resale management, and investment options. Please read these Terms carefully before using the Platform.

2. Definitions
• User: The individual registered on the Platform, including main account holders and Partner accounts.
• Partner: A family or friend account linked to the main User for ticket purchases.
• Referral: An individual introduced to the platform by an existing user, who will become a main user.
• Platform: TT International’s service for ticket advisory, resale, and investment options.
• Rebate: Profit share paid after tickets are resold through the platform, based on the User’s selected investment option.
• Investment Options: Rebate structures offered to users, either 20% or 40%, based on their upfront investment.

3. Partner-Accounts and Consent
• Partner Accounts: Users may create Partner accounts for family or friends. Each Partner account must have a different residential address to comply with event purchase limits and to avoid classification issues. Users must ensure that all Partner account holders have given their explicit consent to use the Platform and have agreed to the terms outlined in this document. TT will only accept Partner accounts once they have signed the consent form sent to them.
• Real People and Fraud Prevention: Users must ensure that Partner accounts are associated with real individuals and accurate personal details. The use of false or fabricated information may constitute fraud or misrepresentation, for which the User will be held accountable.
• Users are responsible for ensuring that all Partner account activities, including ticket purchases, comply with the event organizer’s rules and platform guidelines.

4. Referral Program
• Referral Rebate: Users who participate in the referral program are entitled to 10% of TT’s profit from the resale of tickets purchased by their referred users.
• Activity Requirement: To retain referral status, the main user must attend and purchase tickets for at least 50% of scheduled events (with a minimum of 6 scheduled events per month). If the main user fails to meet this threshold, the 10% referral rebate will be removed for one month. However, the rebate will be restored if the user meets the threshold the following month.

5. Ticket Management and Resale
• All tickets purchased through the Platform must comply with event organizers' ticket purchase limits. Users must not use bots, automated software, or false identities to bypass these limits.
• Personal Buys: If a user intends to personally attend an event, they must notify TT before purchasing the tickets. If the user does not notify TT, the tickets will be treated as part of the resale process. Should the user later decide to attend the event, they will need to purchase tickets at the current market price, which TT can assist with.
• Users agree to forward all relevant ticket details (via email forwarding) to TT for resale.
• Investment Options:
  20% Rebate: No upfront investment, and users receive 20% of the resale value.
  40% Rebate: Requires a 50% upfront deposit, and users receive 40% of the resale value. The deposit is refunded along with the rebate after the event.
• Once tickets are confirmed for resale, ownership is transferred outside the UK for management and distribution.
• Mandatory Use of Resale Services: Users found using TT’s advisory services but not utilizing TT’s resale service risk losing the full value of their portfolio.

6. Fees and Subscription
• Users must subscribe to TT's platform via the app for a monthly fee of £24.99, after a free first month.
• No additional transaction fees apply for ticket resale services.
• Additional In-App Purchases: Users can make additional in-app purchases for services as needed. These purchases will be listed within the app depending on the services required.

7. Investment and Rebates
• Rebates will be processed 10 days after the event has taken place.
• Payment Schedule: Payment runs for all users will occur every 2 weeks, during which the user's "available balance" will be paid out.
• TT reserves the right to modify the rebate structure and notify users at least 30 days in advance.

7A. Compliance Reviews and Payment Holds
• TT reserves the right to carry out compliance, security and verification checks at any time.
• TT may request proof of identity, payment method ownership, source of funds or any other information reasonably required.
• TT may temporarily delay or withhold any payment, rebate or withdrawal whilst compliance checks or investigations are ongoing.
• Failure to provide requested information may result in delayed payments, account suspension or termination.

7B. Disputed Funds
• If TT receives evidence of a dispute, or reasonably believes that a dispute exists, relating to ticket purchases, payment methods, account ownership, resale proceeds or any funds connected with the Platform, TT reserves the right to place the relevant funds on hold.
• Funds may remain on hold until TT is reasonably satisfied that the relevant matter has been resolved or that the funds can be released.
• TT is not responsible for determining legal ownership of disputed funds, and any payment hold does not indicate that TT has taken either party's side.

8. Data Protection and Privacy
• TT International complies with all applicable data protection laws, including the General Data Protection Regulation (GDPR).
• Personal data is collected and processed solely for ticket resale, management, and rebate services.
• Users may request the deletion or modification of their personal data by contacting management@ttinternationalltd.co.uk.
• TT does not share personal data with third parties without explicit consent from the User.

9. Ticket Details, Sales, and Cancellations
• User Responsibility for Ticket Information: Users are responsible for providing accurate and complete information about the tickets, including seat locations, event details, and any restrictions, in accordance with the Consumer Rights Act 2015.
• Finality of Sales: All ticket sales on the platform are final. Once a ticket is sold, the transaction cannot be canceled, refunded, or reversed.
• Event Changes or Cancellations: In the event of any changes, rescheduling, or cancellation of the event, users are responsible for refunding the original value paid for the ticket, including any associated fees.

10. Security of Tickets and Legal Compliance
• Once the TT platform has been used and the user has been paid out, the user is legally obligated to allow TT to deliver the tickets to the end customer.
• This obligation may include providing TT with Gmail access, primary ticket account access, and, where necessary, contacting the event primary (via phone or email).
• In the event that TT does not receive the tickets due to user non-compliance, TT reserves the right to take legal action, including recovering loss of earnings and other related costs.
• Failure to provide the necessary access or cooperation for ticket delivery may result in penalties, including account suspension, fines, or legal action.

11. Referral Non-Compliance and Penalties
• Monetary Penalties: The platform reserves the right to impose monetary penalties for users who fail to fulfill their ticket obligations. These penalties may be deducted from any funds held in the referral's portfolio or account.
• Account Suspension: Non-compliance may result in the temporary suspension of the referral's account on the platform. During this period, the referral will not be able to sell tickets.
• Permanent Account Termination: Persistent failure to fulfill ticket obligations may lead to the permanent termination of the referral’s account on the platform. This termination could be accompanied by the removal of any remaining funds or benefits associated with the account.
• Legal Costs Recovery: In the event of civil action, the referral may be held responsible for covering the platform's legal costs incurred during the proceedings.
• Collection of Compensation: The platform may seek compensation for any damages or losses incurred due to the referral’s non-compliance, in addition to penalties and legal costs.

12. Liability and Indemnity
• TT International provides ticket resale services as an intermediary. We are not liable for penalties, cancellations, or other consequences resulting from a User’s non-compliance with event organizer rules, such as exceeding ticket purchase limits, using automated software, or providing false details.
• This includes consequences from primary markets, such as tickets being assigned as box office collection or primary platforms turning off transfer capabilities, which will be due to exceeding limits or other violations.
• Users indemnify TT against any liability resulting from actions taken under their account or Partner accounts, including ticket purchases made using bots or false representation.
• TT does not store payment card information; all transactions must be completed using the User's own payment methods.
• Users confirm that all payment methods used in connection with the Platform belong to them or are used with the permission of the lawful owner. TT reserves the right to request evidence of this at any time.
• TT shall not be liable for any delay in releasing funds where such delay is necessary to carry out compliance checks or investigate fraud, payment disputes, ticket ownership or resale proceeds.
• Users remain solely responsible for any dispute relating to payment methods, funding of ticket purchases, ticket ownership or entitlement to resale proceeds.

13. Dispute Resolution
• Where a dispute affects funds held by TT, TT reserves the right to retain those funds until TT is reasonably satisfied that the relevant matter has been resolved or that the funds can be released.
• In the event of a dispute, the parties agree to first seek resolution through mediation.
• If mediation fails, disputes may be resolved through the courts of England and Wales, with this Agreement governed by the laws of England and Wales.

14. Amendments to Terms
• TT reserves the right to amend these Terms at any time. Users will be notified at least 30 days in advance of any changes. Continued use of the Platform constitutes acceptance of the updated Terms.

15. Termination
• TT reserves the right to suspend or terminate a User’s account for violations of these Terms, including misuse of the advisory service or non-compliance with event organizer rules.
• Users may cancel their subscription at any time. No refunds will be issued for any unused portion of the subscription.

========================================================

TT INTERNATIONAL PRIVACY POLICY

1. Introduction
At TT International, we are committed to protecting your privacy and ensuring that your personal data is handled in a secure and transparent manner. This Privacy Policy explains how we collect, use, and share your personal information when you use our platform and services. We comply with the General Data Protection Regulation (GDPR) and other relevant laws.

2. Information We Collect
We collect the following types of personal information from users and Collaborator -accounts:
• Personal Details: Full name, email address, phone number, and residential address.
• Payment Information: Payment details for subscription and in-app purchases are handled securely through third-party payment processors.
• Collaborator-Account Information: Personal details of Collaborator -accounts (family and friends), as provided by the main user.
• Usage Data: Information about how you use our platform (e.g., IP address, browser type, login information, etc.).

3. How We Use Your Data
We use the personal information we collect for the following purposes:
• Service Provision: To provide ticket advisory services, manage ticket resales, and process rebates.
• Account Management: To manage user and Collaborator-account details, payments, and communications.
• Improving Our Services: To enhance the platform by analysing user interactions and feedback.
• Legal Compliance: To comply with applicable legal requirements, including data protection laws.

4. Data Sharing
We may share your personal information with:
• Third-Party Service Providers: To process payments or handle email notifications.
• Event Organizers: For ticket purchasing, resales, and event-related matters.
• Collaborator -Account Holders: Personal information may be shared between the main user and Collaborator -accounts for ticketing purposes.
• Legal Authorities: When required to comply with legal or regulatory obligations.

5. Data Retention
We retain personal information only for as long as necessary to fulfil the purposes outlined in this policy, unless a longer retention period is required or permitted by law.

6. Your Rights Under GDPR
You have several rights regarding your personal data:
• Right to Access: You may request access to the personal data we hold about you.
• Right to Correction: You can request corrections to any inaccurate information.
• Right to Erasure: You can request the deletion of your personal data, subject to legal obligations.
• Right to Data Portability: You can request that we provide your data in a structured, machine-readable format.
• Right to Object: You can object to the processing of your personal data in certain circumstances.
To exercise these rights, please contact management@ttinternationalltd.co.uk.

7. Data Security
We implement strict security measures to protect your personal data from unauthorized access, disclosure, or misuse. We use encryption and secure databases to store sensitive information.

8. Changes to the Privacy Policy
We may update this Privacy Policy from time to time to reflect changes in our services or legal obligations. Any changes will be communicated to users, and continued use of the platform constitutes acceptance of the updated policy.

9. Contact Us
If you have any questions about this Privacy Policy or how we handle your personal data, please contact us at management@ttinternationalltd.co.uk.
(end of policy)
  `;

  /**
   * parseTerms() converts the raw text into an array of React elements,
   * selectively applying bold/underline to headings (e.g. lines that match
   * "TT INTERNATIONAL SERVICE TERMS AND CONDITIONS", "TT INTERNATIONAL PRIVACY POLICY",
   * or lines starting with digits and a dot like "1. Introduction").
   * Bullet points (lines starting with "•") are indented.
   */
  const parseTerms = () => {
    // Split into paragraphs by double newlines
    const paragraphs = termsText.split('\n\n');

    return paragraphs.map((paragraph, pIndex) => {
      const lines = paragraph.split('\n').map((line) => line.trim());

      // Check if this entire paragraph is a big heading
      const isBigHeading =
        lines.length === 1 &&
        (lines[0] === 'TT INTERNATIONAL SERVICE TERMS AND CONDITIONS' ||
         lines[0] === 'TT INTERNATIONAL PRIVACY POLICY');

      if (isBigHeading) {
        // Style entire paragraph as a large, bold, underlined heading
        return (
          <p
            key={`p-${pIndex}`}
            style={{
              fontWeight: 'bold',
              textDecoration: 'underline',
              fontSize: '20px',
              margin: '1rem 0',
            }}
          >
            {lines[0]}
          </p>
        );
      }

      // Otherwise, render each line in the paragraph with different styles if needed
      return (
        <div key={`p-${pIndex}`} style={{ marginBottom: '1rem' }}>
          {lines.map((line, lineIndex) => {
            // Subheading check (e.g. "1. Introduction", "2. Definitions", etc.)
            const subheadingRegex = /^\d+\.\s/;
            const isSubheading = subheadingRegex.test(line);

            // Bullet check (line starts with "•")
            const isBullet = line.startsWith('•');

            // Apply styles
            let style = { marginBottom: '0.4rem', lineHeight: '1.5' };

            if (isSubheading) {
              style = {
                ...style,
                fontWeight: 'bold',
                textDecoration: 'underline',
                marginTop: '0.8rem',
              };
            } else if (isBullet) {
              style = {
                ...style,
                marginLeft: '1.5rem',
              };
            }

            return (
              <p key={`p-${pIndex}-l-${lineIndex}`} style={style}>
                {line}
              </p>
            );
          })}
        </div>
      );
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setHasScrolledToBottom(true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAccept = () => {
    onAccept();
    onClose();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.title}>Terms & Conditions and Privacy Policy</h2>
        <div ref={scrollContainerRef} style={styles.scrollContainer}>
          {parseTerms()}
        </div>
        <div style={styles.buttonRow}>
          <button onClick={onClose} style={{ marginRight: '8px' }}>
            Close
          </button>
          <button onClick={handleAccept} disabled={!hasScrolledToBottom}>
            {hasScrolledToBottom ? 'Accept' : 'Scroll to bottom to accept'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: '#fff',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    borderRadius: '8px',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    marginBottom: '1rem',
    textAlign: 'center',
    fontSize: '22px',
    fontWeight: 'bold',
  },
  scrollContainer: {
    overflowY: 'auto',
    border: '1px solid #ccc',
    padding: '1rem',
    marginBottom: '1rem',
    height: '500px', // Make the scrollable area larger
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
};

export default TermsModal;
